(() => {
 if (customElements.get('elmsett-scroll-story')) return;
 const clamp = (v, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
 const ease = (q, a, b) => { const x = clamp((q - a) / (b - a)); return x * x * (3 - 2 * x); };
 const STEPS = {grow: [.05, .22], push: [.27, .40], texture: [.45, .62], third: [.70, .87], hold: [.87, 1]};
 const QUIET = '(prefers-reduced-motion: reduce), (max-height: 499px)';
 const VIEWED = 'elmsett_story_rug_viewed', CLICKED = 'elmsett_story_discover';

 class ElmsettScrollStory extends HTMLElement {
  connectedCallback() {
   this.disconnectedCallback();
   const lifetime = this.lifetime = new AbortController(), signal = lifetime.signal;
   this.stage = this.querySelector('[data-stage]');
   this.rugs = [...this.querySelectorAll('[data-rug]')];
   this.written = new Map();
   this.viewed = new Set();
   this.progress = 0;
   this.readRatios();
   this.quiet = matchMedia(QUIET);
   this.quiet.addEventListener('change', () => this.apply(), {signal});
   this.addEventListener('click', event => this.onClick(event), {signal});
   this.addEventListener('focusin', event => this.onFocus(event), {signal});
   document.addEventListener('shopify:block:select', event => this.onSelect(event), {signal});
   this.nearby = new IntersectionObserver(entries => this.onNearby(entries), {rootMargin: '100% 0px'});
   this.nearby.observe(this);
   this.apply();
  }

  disconnectedCallback() {
   this.lifetime?.abort();
   this.stopListening();
   this.nearby?.disconnect();
   this.stillViews?.disconnect();
  }

  get enhanced() { return this.hasAttribute('data-enhanced'); }

  readRatios() {
   for (const shot of this.querySelectorAll('[data-shot]')) {
    const img = shot.querySelector('img'), w = Number(img?.getAttribute('width')), h = Number(img?.getAttribute('height'));
    if (w > 0 && h > 0) shot.style.setProperty('--ar', (w / h).toFixed(4));
   }
  }

  apply() {
   const on = this.dataset.motion !== 'false' && !this.quiet.matches && this.rugs.length > 0 && !!this.stage && CSS.supports('container-type', 'size');
   this.toggleAttribute('data-enhanced', on);
   this.toggleAttribute('data-es-smart-header-avoid', on);
   this.stillViews?.disconnect();
   if (on) {
    this.sharpenRooms();
    if (this.isNear) this.listen();
    this.update();
   } else {
    this.stopListening();
    for (const rug of this.rugs) for (const name of ['--ss-grow', '--ss-push', '--ss-texture', '--ss-third', '--ss-hold', '--ss-shift']) rug.style.removeProperty(name);
    this.written.clear();
    this.watchStillViews();
   }
  }

  // The push-in may use the largest bundled room photograph; request it only for wide stages.
  sharpenRooms() {
   const wide = innerWidth >= 750;
   for (const rug of this.rugs) {
    const room = rug.querySelector('[data-shot="room"]'), width = room?.offsetWidth || 0;
    if (!width) continue;
    const sizes = Math.ceil(width * (wide ? 1.2 : 1)) + 'px';
    for (const img of rug.querySelectorAll('.es-ss__wash img, [data-shot="room"] img')) {
     const full = img.dataset.fullSrcset;
     if (full && img.getAttribute('srcset') !== full) img.setAttribute('srcset', full);
     if (img.hasAttribute('srcset') && parseInt(img.getAttribute('sizes'), 10) !== parseInt(sizes, 10)) img.setAttribute('sizes', sizes);
    }
   }
  }

  onNearby(entries) {
   this.isNear = entries.some(entry => entry.isIntersecting);
   if (this.isNear && !this.loaded) {
    this.loaded = true;
    for (const img of this.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
   }
   if (this.isNear && this.enhanced) { this.listen(); this.update(); } else this.stopListening();
  }

  listen() {
   if (this.listening) return;
   const listening = this.listening = new AbortController(), signal = listening.signal;
   const schedule = () => { if (this.queued) return; this.queued = true; this.frame = requestAnimationFrame(() => { this.queued = false; this.update(); }); };
   addEventListener('scroll', schedule, {passive: true, signal});
   addEventListener('resize', () => { this.sharpenRooms(); schedule(); }, {passive: true, signal});
   addEventListener('pageshow', schedule, {signal});
  }

  stopListening() {
   this.listening?.abort();
   this.listening = null;
   cancelAnimationFrame(this.frame);
   this.queued = false;
  }

  geometry() {
   const stage = this.stage.offsetHeight, rect = this.getBoundingClientRect(), n = this.rugs.length;
   const distance = Math.max(1, rect.height - stage);
   const gap = n > 1 ? Math.min(stage, distance / (n * 3)) : 0;
   return {rect, stage, n, distance, gap, story: (distance - gap * (n - 1)) / n};
  }

  write(el, name, value) {
   const text = value.toFixed(4), key = el.id + name;
   if (this.written.get(key) === text) return;
   this.written.set(key, text);
   el.style.setProperty(name, text);
  }

  update() {
   if (!this.enhanced) return;
   const {rect, n, distance, gap, story} = this.geometry();
   const u = clamp(-rect.top, 0, distance);
   this.progress = u / distance;
   this.rugs.forEach((rug, i) => {
    const start = i * (story + gap);
    const q = clamp((u - start) / story);
    const leave = i < n - 1 ? clamp((u - start - story) / gap) : 0;
    const arrive = i > 0 ? clamp((u - start + gap) / gap) : 1;
    this.write(rug, '--ss-shift', (1 - arrive) - leave);
    for (const [name, [a, b]] of Object.entries(STEPS)) this.write(rug, '--ss-' + name, ease(q, a, b));
    if (q >= STEPS.hold[0]) this.viewedRug(rug, 'motion');
   });
   this.write(this, '--ss-progress', this.progress);
  }

  watchStillViews() {
   const bars = this.rugs.map(rug => rug.querySelector('.es-ss__bar')).filter(Boolean);
   if (!bars.length) return;
   this.stillViews = new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) this.viewedRug(entry.target.closest('[data-rug]'), 'static');
   }, {threshold: .9});
   bars.forEach(bar => this.stillViews.observe(bar));
  }

  where(rug) {
   return {rug: rug.dataset.handle, position: Number(rug.dataset.position)};
  }

  publish(name, detail) {
   try { window.Shopify?.analytics?.publish?.(name, detail); } catch (error) { /* Analytics must never block browsing. */ }
   this.dispatchEvent(new CustomEvent('elmsett:scroll-story', {bubbles: true, detail: {name, ...detail}}));
  }

  viewedRug(rug, mode) {
   if (!rug || this.viewed.has(rug)) return;
   this.viewed.add(rug);
   this.publish(VIEWED, {...this.where(rug), of: this.rugs.length, mode});
  }

  onClick(event) {
   const link = event.target.closest('a[data-ss-link]'), rug = link?.closest('[data-rug]');
   if (!rug) return;
   this.publish(CLICKED, {...this.where(rug), target: link.dataset.ssLink});
  }

  // Keyboard focus and theme-editor selection move the page to the rug's settled frame.
  showRug(rug) {
   const index = this.rugs.indexOf(rug);
   if (index < 0) return;
   if (!this.enhanced) { rug.scrollIntoView({block: 'nearest'}); return; }
   const {rect, gap, story} = this.geometry();
   const top = scrollY + rect.top + index * (story + gap) + story * .95;
   if (Math.abs(scrollY - top) > 2) scrollTo({top, behavior: 'instant'});
   this.update();
  }

  onFocus(event) {
   const rug = event.target.closest?.('[data-rug]');
   if (!rug || !this.enhanced || !event.target.matches(':focus-visible')) return;
   requestAnimationFrame(() => { if (Math.abs(Number(rug.style.getPropertyValue('--ss-shift')) || 0) > .001) this.showRug(rug); });
  }

  onSelect(event) {
   const rug = this.rugs.find(item => item.dataset.blockId === event.detail?.blockId);
   if (rug) this.showRug(rug);
  }
 }

 customElements.define('elmsett-scroll-story', ElmsettScrollStory);
})();
